import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
// Redux
import { useSelector } from "react-redux";
// Constants - Functions
import { pathHomePage, WEB_SERVICE_MIMS, protocols, applications } from "../../../../config/constants";
import { diagnosisProtocols, isDiagnosisProtocol } from "../../../../config/validateProtocols";
// Components
import { MainContainer, BackLink, ArrowImg, LinkText, PreviousAndNextDiv, NavigationButton, SeparatorDiv } from "./styles";
import Axios from "../../../../config/axios";
// Assets
import ArrowBackImg from "../../../../assets/icons/ico_arrow_back.svg";
import ArrowLeftImg from "../../../../assets/icons/ico_arrow_left_bold.svg";
import ArrowRightImg from "../../../../assets/icons/ico_arrow_right_bold.svg";

const ProtocolNavigation = () => {
  const { patientId, protocolId, software } = useParams();
  const [currentProtocols, setCurrentProtocols] = useState([]);
  const clinicianToken = useSelector(({ clinician }) => clinician.data.token);
  const hospitalId = useSelector(({ clinician }) => clinician.data.HOSPITAL_ID);
  const [t] = useTranslation("global");

  useEffect(() => {
    if (isDiagnosisProtocol(protocolId)) {
      return setCurrentProtocols(diagnosisProtocols.map((diagnosisProtocol) => diagnosisProtocol.id));
    }

    const handleAddProtocol = (protocolsArray, activeProtocols, hospitalProtocols) => {
      protocolsArray.forEach((protocol) => {
        if (hospitalProtocols.includes(protocol.id) && protocol.isActive) {
          activeProtocols.push(protocol.id);
        }
      });
    };

    const handleSetCurrentProtocols = (softwareId, protocolsBySoftwareParams) => {
      Axios("get", `${WEB_SERVICE_MIMS}/hospitals/protocols/${softwareId}/${hospitalId}`)
        .then(({ data }) => {
          const hospitalProtocols = [];
          data.forEach((protocol) => {
            hospitalProtocols.push(protocol.PROTOCOL_ID);
          });
          const protocolsBySoftware = Object.values(protocolsBySoftwareParams);
          const activeProtocols = [];
          handleAddProtocol(protocolsBySoftware, activeProtocols, hospitalProtocols);
          setCurrentProtocols(activeProtocols);
        })
        .catch((error) => {
          console.log("Get hospital protocols: ", error);
        });
    };

    const handleSetCurrentWebProtocols = () => {
      Axios("get", `${WEB_SERVICE_MIMS}/hospitals/protocols/${applications.web.id}/${hospitalId}`)
        .then(({ data }) => {
          const hospitalProtocols = [];
          data.forEach((protocol) => {
            hospitalProtocols.push(protocol.PROTOCOL_ID);
          });
          const handTrackingProtocols = Object.values(protocols.web.handTracking);
          const fullBodyProtocols = Object.values(protocols.web.fullBody);
          const protocolsBySoftware = handTrackingProtocols.concat(fullBodyProtocols);
          const activeProtocols = [];
          handleAddProtocol(protocolsBySoftware, activeProtocols, hospitalProtocols);
          setCurrentProtocols(activeProtocols);
        })
        .catch((error) => {
          console.log("Get hospital protocols: ", error);
        });
    };

    const handleSetCurrentAppProtocols = () => {
      Axios("get", `${WEB_SERVICE_MIMS}/hospitals/protocols/${applications.app.id}/${hospitalId}`)
        .then(({ data }) => {
          const hospitalProtocols = [];
          data.forEach((protocol) => {
            hospitalProtocols.push(protocol.PROTOCOL_ID);
          });
          const basicProtocols = Object.values(protocols.app.basic);
          const tensorFlowProtocols = Object.values(protocols.app.rotation);
          const arProtocols = Object.values(protocols.app.ar);
          const protocolsBySoftware = basicProtocols.concat(tensorFlowProtocols, arProtocols);
          const activeAppProtocols = [];
          handleAddProtocol(protocolsBySoftware, activeAppProtocols, hospitalProtocols);
          setCurrentProtocols(activeAppProtocols);
        })
        .catch((error) => {
          console.log("Get hospital protocols: ", error);
        });
    };

    const handleSetCurrentPlusProtocols = () => {
      Axios("get", `${WEB_SERVICE_MIMS}/hospitals/protocols/${applications.plus.id}/${hospitalId}`)
        .then(({ data }) => {
          const hospitalProtocols = [];
          data.forEach((protocol) => {
            hospitalProtocols.push(protocol.PROTOCOL_ID);
          });
          const basicProtocols = Object.values(protocols.app.basic);
          const rotationProtocols = Object.values(protocols.app.rotation);
          const arProtocols = Object.values(protocols.app.ar);
          const handTrackingProtocols = Object.values(protocols.web.handTracking);
          const fullBodyProtocols = Object.values(protocols.web.fullBody);
          const protocolsBySoftware = basicProtocols.concat(rotationProtocols, arProtocols, handTrackingProtocols, fullBodyProtocols);
          const activeAppProtocols = [];
          handleAddProtocol(protocolsBySoftware, activeAppProtocols, hospitalProtocols);
          setCurrentProtocols(activeAppProtocols);
        })
        .catch((error) => {
          console.log("Get hospital protocols: ", error);
        });
    };

    switch (software) {
      case "clinic":
        handleSetCurrentProtocols(applications.clinic.id, protocols.clinicAndHome);
        break;
      case "home":
        handleSetCurrentProtocols(applications.home.id, protocols.clinicAndHome);
        break;
      case "web":
        handleSetCurrentWebProtocols();
        break;
      case "app":
        handleSetCurrentAppProtocols();
        break;
      case "plus":
        handleSetCurrentPlusProtocols();
        break;
      default:
        break;
    }

    return () => {
      setCurrentProtocols([]);
    };
  }, [hospitalId, protocolId, software]);

  const newUrlToReports = (protocolId) => {
    window.location.href = `${pathHomePage}/reports/${clinicianToken}/${software}/${patientId}/${protocolId}`;
  };

  const handleRedirectPreviousProtocol = () => {
    const previousProtocolIdIndex = currentProtocols.indexOf(Number(protocolId)) - 1;
    const previousProtocolId = currentProtocols.at(previousProtocolIdIndex);
    newUrlToReports(previousProtocolId);
  };

  const handleRedirectNextProtocol = () => {
    const nextProtocolIdIndex = currentProtocols.indexOf(Number(protocolId)) + 1;
    let nextProtocolId;
    if (nextProtocolIdIndex === currentProtocols.length) {
      nextProtocolId = currentProtocols.shift();
    } else {
      nextProtocolId = currentProtocols.at(nextProtocolIdIndex);
    }
    newUrlToReports(nextProtocolId);
  };

  const renderBackLink = () => {
    const redirectLink = isDiagnosisProtocol(protocolId)
      ? `${pathHomePage}/diagnosis-prescriptions/${clinicianToken}/${patientId}/${software}`
      : `${pathHomePage}/prescriptions/${software}/${clinicianToken}/${patientId}`;

    return (
      <BackLink to={redirectLink}>
        <ArrowImg src={ArrowBackImg} alt="ico-arrow-back" margin="0 10px 0 0" margin960="0 5px 0 0" />
        <LinkText>{t("activities")}</LinkText>
      </BackLink>
    );
  };

  return (
    <MainContainer>
      {renderBackLink()}
      <PreviousAndNextDiv>
        <NavigationButton onClick={handleRedirectPreviousProtocol}>
          <ArrowImg src={ArrowLeftImg} alt="ico-arrow-left" margin="0 10px 0 0" margin960="0 5px 0 0" />
          <LinkText>{t("previous")}</LinkText>
        </NavigationButton>
        <SeparatorDiv />
        <NavigationButton onClick={handleRedirectNextProtocol}>
          <LinkText>{t("next")}</LinkText>
          <ArrowImg src={ArrowRightImg} alt="ico-arrow-right" margin="0 0 0 10px" margin960="0 0 0 5px" />
        </NavigationButton>
      </PreviousAndNextDiv>
    </MainContainer>
  );
};

export default ProtocolNavigation;
